import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  fetchPoolCallerIds,
  type AdminPhoneNumber,
  type AdminPool,
  type UsageWindow,
  type PoolNumberRow,
} from './api';
import { isActiveNumber } from './board-state';

// "Assigned to this pool" has no answer — the inventory is global — so this lists all of
// it with each number's dials for THIS pool joined on. The unused ones stay: dropping
// them would render an idle pool as owning nothing to dial with.
const WINDOW: UsageWindow = '7d';

type Row = {
  number: string;
  status: string;
  // null when the usage read has not answered: unknown, which the table must not
  // render as the zero that means "owned but never dialled".
  dials: number | null;
  calls: number | null;
  human_pct: number | null;
};

// The inventory left-joined with this pool's usage. Not a filter: a number with no
// dials is a real answer ("available, unused"), so it stays in the list at 0.
function buildRows(inventory: AdminPhoneNumber[], used: PoolNumberRow[] | null): Row[] {
  const byNumber = new Map((used ?? []).map((u) => [u.number, u]));

  return inventory
    .map((n) => {
      const u = byNumber.get(n.number);
      return {
        number: n.number,
        status: n.status,
        dials: used === null ? null : (u?.dials ?? 0),
        calls: used === null ? null : (u?.calls ?? 0),
        human_pct: u?.human_pct ?? null,
      };
    })
    .sort((a, b) => (b.dials ?? -1) - (a.dials ?? -1) || a.number.localeCompare(b.number));
}

function pct(v: number | null): string {
  return v == null ? '—' : `${Math.round(v)}%`;
}

export function PoolNumbersTab({
  pool,
  numbers,
}: {
  pool: AdminPool;
  numbers: AdminPhoneNumber[];
}) {
  const [used, setUsed] = useState<PoolNumberRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    // Unknown, not empty: the last pool's counts must not sit under a new pool's name,
    // and zero would claim these numbers were never dialled rather than never asked about.
    setUsed(null);
    fetchPoolCallerIds(pool.id, WINDOW)
      .then((report) => setUsed(report.numbers))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [pool.id]);

  useEffect(load, [load]);

  const rows = useMemo(() => buildRows(numbers, used), [numbers, used]);
  const activeCount = numbers.filter(isActiveNumber).length;
  const usedCount = rows.filter((r) => (r.dials ?? 0) > 0).length;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <p className="text-muted-foreground text-xs">
        Caller IDs are one global inventory — this pool can dial from any of the{' '}
        <b className="text-foreground">{activeCount}</b> active number
        {activeCount === 1 ? '' : 's'}, and the dialer picks the best regional match per call.{' '}
        {/* Waits for data: unanswered, usedCount is 0 for want of an answer, not of dials. */}
        {used === null ? null : usedCount > 0 ? (
          <>
            It has used <b className="text-foreground">{usedCount}</b> of them in the last 7 days.
          </>
        ) : (
          <>It has not dialed in the last 7 days.</>
        )}
      </p>

      {err && <p className="text-destructive text-xs">⚠ {err}</p>}

      <div className="flex-1 overflow-y-auto rounded-md border">
        <table className="w-full text-right text-xs tabular-nums">
          <thead className="bg-muted/50 text-muted-foreground sticky top-0 z-10">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Number</th>
              <th className="px-2 py-1.5 text-left font-medium">Status</th>
              <th className="px-2 py-1.5 font-medium" title="Dials this pool made from it (7d)">
                Dials
              </th>
              <th className="px-2 py-1.5 font-medium" title="Answered calls (7d)">
                Calls
              </th>
              <th
                className="px-2 py-1.5 font-medium"
                title="Confirmed human conversation, of answered calls"
              >
                Human %
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.number}
                className={`border-t ${r.dials === 0 ? 'text-muted-foreground' : ''}`}
              >
                <td className="px-2 py-1.5 text-left font-mono">{r.number}</td>
                <td className="px-2 py-1.5 text-left">
                  <span className="bg-muted rounded-full px-2 py-0.5 text-[10px]">{r.status}</span>
                </td>
                <td className="px-2 py-1.5">
                  {r.dials === 0 ? <span className="italic">not used yet</span> : (r.dials ?? '—')}
                </td>
                <td className="px-2 py-1.5">{r.dials ? r.calls : '—'}</td>
                <td className="px-2 py-1.5">{pct(r.human_pct)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted-foreground py-6 text-center italic">
                  {loading
                    ? 'Loading…'
                    : 'No caller IDs owned yet — buy some on the Numbers screen.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
